/**
 * settlementTrees.ts — seeded street-tree placement for settlements
 * (2026-07-31, user ask: "trees, the same as from nearby biomes, should be in
 * settlements"). Pure/deterministic (salt 0x51e3), the streetlights idiom:
 * interval sites along non-tunnel road edges, offset BEYOND the carriageway,
 * REJECTED when they land inside any parcel polygon — so a tree can never
 * stand in a building or on a road (the through-the-roof tree bug class stays
 * impossible by construction). Species/colour are NOT chosen here: the client
 * resolves them from the local biome via its flora library, which is exactly
 * what makes settlement trees match the surrounding forests.
 */
import * as pmath from "../pmath.js";
import { hashSeed } from "../rng.js";
import { hashInts, h01 } from "./hash.js";
import { packCell } from "./cells.js";
import { type CityGraph, localToDir, EDGE_TUNNEL } from "./citygraph.js";
import { pointInPolygon } from "./lots.js";
import type { PlanetSampler } from "../universe.js";

/** [x, y, z (planet-local base), roll (0..1 — variant/species pick), scaleK, yaw]. */
export const SETTLEMENT_TREE_STRIDE = 6;
/** Nominal interval along a road edge (metres). 34 → 16 (2026-08-02 user call:
 *  trees MUCH more common in every settlement type). */
export const SETTLEMENT_TREE_SPACING_M = 16;
/** Base clearance beyond the carriageway edge (metres); up to +3.5 more per tree. */
export const SETTLEMENT_TREE_MARGIN_M = 3.0;
const SALT = 0x51e3;
/** Keep-out inflation around parcel bounding boxes (metres) — canopy clearance. */
const PARCEL_PAD_M = 1.5;

export function buildSettlementTrees(g: CityGraph, sampler: PlanetSampler): Float64Array {
  const frame = g.frame;
  const R = sampler.def.radius;
  const planetHash = hashSeed(sampler.def.seed);
  const rk = packCell(g.regionId);
  const out: number[] = [];
  const d: number[] = [0, 0, 0];

  // Parcel keep-out: padded bounding boxes prefilter, exact polygon confirms.
  const nP = g.parcels.length;
  const bb = new Float64Array(nP * 4);
  for (let p = 0; p < nP; p++) {
    const poly = g.parcels[p].poly;
    let u0 = Infinity, v0 = Infinity, u1 = -Infinity, v1 = -Infinity;
    for (let i = 0; i < poly.length; i += 2) {
      if (poly[i] < u0) u0 = poly[i];
      if (poly[i] > u1) u1 = poly[i];
      if (poly[i + 1] < v0) v0 = poly[i + 1];
      if (poly[i + 1] > v1) v1 = poly[i + 1];
    }
    bb[p * 4] = u0 - PARCEL_PAD_M; bb[p * 4 + 1] = v0 - PARCEL_PAD_M;
    bb[p * 4 + 2] = u1 + PARCEL_PAD_M; bb[p * 4 + 3] = v1 + PARCEL_PAD_M;
  }
  // Road keep-out (2026-08-02: "trees are in the middle of the road"): a site
  // offset off ITS OWN edge can still land on ANOTHER road near junctions or
  // parallel streets — at the new density that happened constantly. Reject any
  // site within (that edge's half-width + clearance) of ANY road centreline.
  const ROAD_CLEAR_M = 1.6;
  const onRoad = (u: number, v: number): boolean => {
    for (let e = 0; e < g.edgeKind.length; e++) {
      if (g.edgeKind[e] === EDGE_TUNNEL) continue;
      const a = g.edgeA[e], b = g.edgeB[e];
      const ax = g.nodeU[a], ay = g.nodeV[a];
      const dx = g.nodeU[b] - ax, dy = g.nodeV[b] - ay;
      const l2 = dx * dx + dy * dy;
      let t = l2 > 1e-12 ? ((u - ax) * dx + (v - ay) * dy) / l2 : 0;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const qx = ax + dx * t - u, qy = ay + dy * t - v;
      const keep = (g.edgeWidth[e] > 0 ? g.edgeWidth[e] : 5) * 0.5 + ROAD_CLEAR_M;
      if (qx * qx + qy * qy < keep * keep) return true;
    }
    return false;
  };
  const inParcel = (u: number, v: number): boolean => {
    for (let p = 0; p < nP; p++) {
      const o = p * 4;
      if (u < bb[o] || u > bb[o + 2] || v < bb[o + 1] || v > bb[o + 3]) continue;
      const poly = g.parcels[p].poly;
      if (pointInPolygon(poly, u, v)) return true;
      // Outside the poly but inside the padded box: reject only when actually
      // within the pad of an EDGE (a plain bbox test swallowed the whole
      // roadside strip — parcels front the roads).
      for (let i = 0; i < poly.length; i += 2) {
        const j = (i + 2) % poly.length;
        const ax = poly[i], ay = poly[i + 1];
        const dx = poly[j] - ax, dy = poly[j + 1] - ay;
        const l2 = dx * dx + dy * dy;
        let t = l2 > 1e-12 ? ((u - ax) * dx + (v - ay) * dy) / l2 : 0;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        const qx = ax + dx * t - u, qy = ay + dy * t - v;
        if (qx * qx + qy * qy < PARCEL_PAD_M * PARCEL_PAD_M) return true;
      }
    }
    return false;
  };

  for (let e = 0; e < g.edgeKind.length; e++) {
    if (g.edgeKind[e] === EDGE_TUNNEL) continue;
    const a = g.edgeA[e], b = g.edgeB[e];
    const au = g.nodeU[a], av = g.nodeV[a], bu = g.nodeU[b], bv = g.nodeV[b];
    const du = bu - au, dv = bv - av;
    const len = pmath.hypot(du, dv);
    if (len < SETTLEMENT_TREE_SPACING_M * 0.55) continue;
    const tu = du / len, tv = dv / len; // along
    const nu = -tv, nv = tu; // normal
    const half = (g.edgeWidth[e] > 0 ? g.edgeWidth[e] : 5) * 0.5;
    // Short street segments (the common case in dense grids) still host one
    // candidate mid-edge.
    const steps = Math.max(1, Math.floor(len / SETTLEMENT_TREE_SPACING_M));
    for (let s = 1; s <= steps; s++) {
      // 2026-08-02 density pass (user: "trees much more common EVERYWHERE"):
      // BOTH road sides roll a candidate (independent hashes), gaps rarer.
      for (let sideIdx = 0; sideIdx < 2; sideIdx++) {
        const hs = hashInts(planetHash ^ SALT, rk, e, s * 2 + sideIdx);
        if (h01(hs) < 0.15) continue; // occasional gaps keep it an avenue, not a hedge
        const along = (s - 0.35 + h01(hs ^ 0x9e37) * 0.7) * SETTLEMENT_TREE_SPACING_M;
        if (along > len) continue;
        const side = sideIdx === 0 ? 1 : -1;
        const off = half + SETTLEMENT_TREE_MARGIN_M + h01(hs ^ 0x51e3) * 3.5;
        const u = au + tu * along + nu * off * side;
        const v = av + tv * along + nv * off * side;
        if (inParcel(u, v) || onRoad(u, v)) continue;
        localToDir(frame, u, v, d);
        const h = sampler.height(d[0], d[1], d[2]);
        const baseR = R + h;
        out.push(
          d[0] * baseR, d[1] * baseR, d[2] * baseR,
          h01(hs ^ 0x77aa),                       // roll: species/variant pick
          // scaleK 0.85..1.4 → 1.35..2.35 (2026-08-02): settlement trees ran at
          // HALF the forest scatter's scale — same species, but they read as a
          // different (dwarf) tree. Now matches the surrounding forest stands.
          1.35 + h01(hs ^ 0x1234) * 1.0,
          h01(hs ^ 0xabcd) * Math.PI * 2,         // yaw
        );
      }
    }
  }
  return Float64Array.from(out);
}
